import * as Sentry from '@sentry/browser';
import merge from 'lodash/merge.js';

import axios from '@/plugins/axios.js';
import Note from '@/store/modules/notes/Note.js';
import Collection from '@/store/quill/Collection.js';
import EdverumTable from '@/store/utilities/EdverumTable.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';

const collectionHelpers = EdverumTable.collection('note');

export default merge(Collection('Notes', Note), {
    state: {
        sort: {
            title: 'asc',
            created_at: null,
            updated_at: null,
        },
        checkedRows: [],
    },

    getters: {
        ...collectionHelpers.getters,
        checkedRows: state => state.checkedRows,
    },

    actions: {
        fetch: collectionHelpers.actions.fetch(({ context, queryBuilder }) => {
            const { getters } = context;
            const { courseId } = context.state;

            queryBuilder
                .include(['course', 'activity', 'tags', 'media'])
                .sort(getters.queryBuilderSorting)
                .setPagination(getters.paginate);

            if (courseId) {
                queryBuilder.filter('course_id', courseId);
            }
        }),

        fetchOne: collectionHelpers.actions.fetchOne(({ queryBuilder }) => {
            queryBuilder.include(['course', 'activity', 'tags', 'media']);
        }),

        addNote: collectionHelpers.actions.addModel(({ queryBuilder }) => {
            queryBuilder.include(['course', 'activity', 'tags', 'media']);
        }),

        updateNote: collectionHelpers.actions.updateModel(),

        removeChecked: collectionHelpers.actions.removeChecked(),

        clearSort: collectionHelpers.actions.clearSort,

        paginateAndFetch: collectionHelpers.actions.paginateAndFetch,

        updateCheckedRows({ commit }, rows) {
            commit('SET_CHECKED_ROWS', rows);
        },

        async saveNote({ dispatch }, note) {
            const queryBuilder = new QueryBuilder('notes');
            queryBuilder.include(['course', 'activity', 'tags', 'media']);

            if (note.id) {
                await dispatch('updateNote', { id: note.id, data: note });
            } else {
                await dispatch('addNote', note);
            }

            await dispatch('fetch');
        },

        async deleteNote({ dispatch }, id) {
            await dispatch('remove', id);
            await dispatch('fetch');
        },

        async getNotes({ dispatch }, payload) {
            try {
                const filterPayload = Object.entries(payload)
                    .filter(([, value]) => value != null)
                    .map(([key, value]) => ({
                        key,
                        value,
                    }));

                const response = await dispatch('fetchAll', {
                    endpoint: 'notes',
                    filters: filterPayload,
                });

                if (!response) {
                    return [];
                }

                response.data.data.forEach(item => {
                    if (item.tags) {
                        item.tags = item.tags.filter(
                            (tag, index, self) => tag && self.findIndex(t => t && t.id === tag.id) === index
                        );
                        delete item.tags;
                    }
                    dispatch('add', item);
                });
                return response.data.data;
            } catch (error) {
                Sentry.captureException(error, {
                    tags: {
                        'crash-report': false,
                    },
                });
                return [];
            }
        },
        async getAllNotes() {
            return axios.get('/api/notes');
        },
        async getActivity({ dispatch }, payload) {
            if (payload.activity_id) {
                return dispatch('fetchAll', {
                    endpoint: 'activities',
                    filters: [
                        { key: 'course_id', value: payload.course_id },
                        { key: 'id', value: payload.activity_id },
                    ],
                    raw: { menu: true },
                });
            }
            return null;
        },

        async getCourse({ dispatch }, payload) {
            await dispatch('fetchAll', {
                endpoint: `courses/${payload.id}`,
            }).then(resp => resp.data);
        },

        async addNotes({ dispatch }, note) {
            try {
                const { data } = await axios.post('/api/notes', {
                    title: note.title,
                    body: note.body,
                    activity_id: note.activityId,
                    course_id: note.courseId,
                    tags: note.tags,
                    media: note.media,
                });
                if (data.data.tags) {
                    data.data.tags = data.data.tags.filter(
                        (tag, index, self) => tag && self.findIndex(t => t && t.id === tag.id) === index
                    );
                    delete data.data.tags;
                }
                dispatch('add', data.data);
                dispatch('updateTags', { subject: data.data, tags: note.tags });
                return data;
            } catch (error) {
                Sentry.captureException(error, {
                    tags: {
                        'crash-report': false,
                    },
                });
                return null;
            }
        },
        async deleteNotes({ getters, dispatch }, noteId) {
            try {
                await dispatch('remove', noteId);
                await dispatch('fetch');
            } catch (error) {
                console.error('Error deleting note:', error);
                throw error;
            }
        },

        async refreshTable({ dispatch, state }) {
            try {
                await dispatch('fetch');
            } catch (error) {
                console.error('[Notes] refreshTable - error:', error);
                try {
                    const Note = (await import('@/models/Note.js')).default;
                    const Tag = (await import('@/models/Tag.js')).default;
                    const Taggable = (await import('@/models/Taggable.js')).default;

                    Note.deleteAll();
                    Tag.deleteAll();
                    Taggable.deleteAll();

                    await new Promise(resolve => setTimeout(resolve, 100));
                    await dispatch('fetch');
                } catch (fallbackError) {
                    console.error('[Notes] refreshTable - fallback error:', fallbackError);
                }
            }
        },

        async gentleRefresh({ dispatch }) {
            try {
                await dispatch('fetch');
            } catch (error) {
                console.error('[Notes] gentleRefresh - error:', error);
            }
        },

        async fetchAssignableTags({ rootGetters, commit }) {
            const userId = rootGetters['Auth/userId'];
            if (!userId) {
                return;
            }

            const { data } = await axios.get(`/api/tags?type=notes-${userId}`);
            if (data.data.count > 0) {
                commit('setAssignableTags', data.data);
            }
        },

        async fetchNotesWithoutCourse() {
            const queryBuilder = new QueryBuilder('notes');
            queryBuilder.filters({ key: 'without_course', value: 1 });
            queryBuilder.include(['tags', 'media', 'activity']);
            return axios.get(queryBuilder.toUrl());
        },
        updateTags: collectionHelpers.actions.updateNoteTags(),
        addModel() {
            return Promise.resolve();
        },
        updateModel() {
            return Promise.resolve();
        },
    },

    mutations: {
        /**
         * Updates a model in the store with new data
         * @param {Object} state - Vuex state object
         * @param {Object} payload - The data to update the model with
         */
        UPDATE_MODEL: collectionHelpers.mutations.UPDATE_MODEL,

        /**
         * Clears the sorting state of the notes table
         * @param {Object} state - Vuex state object
         */
        CLEAR_SORTING: collectionHelpers.mutations.CLEAR_SORTING,

        /**
         * Updates the array of checked rows in the notes table
         * @param {Object} state - Vuex state object
         * @param {Array} rows - Array of checked row objects
         */
        SET_CHECKED_ROWS(state, rows) {
            state.checkedRows = rows;
        },

        /**
         * Updates the assignable tags for notes
         * @param {Object} state - Vuex state object
         * @param {Array} tags - Array of tag objects that can be assigned to notes
         */
        setAssignableTags(state, tags) {
            state.NoteTags.assignableTags = tags;
        },

        /**
         * Updates tags for a note
         * This mutation is handled by the autosaver plugin
         * @param {Object} state - Vuex state object
         */
        UPDATE_TAGS() {},

        /**
         * Deletes media from a note
         * This mutation is handled by the autosaver plugin
         * @param {Object} state - Vuex state object
         */
        DELETE_MEDIA() {},

        /**
         * Handles note edits
         * This mutation is handled by the autosaver plugin
         * @param {Object} state - Vuex state object
         */
        EDIT() {},
    },
});
